"use client";

import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useMemo } from "react";
import { useAccount, useBalance } from "wagmi";

export type WalletConnectionState = "disconnected" | "connecting" | "connected" | "switching";

interface WalletState {
  isConnected: boolean;
  isConnecting: boolean;
  connectionState: WalletConnectionState;
  address?: string;
  shortAddress?: string;
  username?: string | null;
  nativeBalance?: string;
  nativeBalanceFormatted?: string;
  nativeSymbol?: string;
  chainId?: number;
  chainName?: string;
  isWrongNetwork?: boolean;
  disconnect: () => void;
}

export function useWalletState(targetChainId?: number): WalletState {
  const { primaryWallet, handleLogOut } = useDynamicContext();
  const { address, chainId, isConnecting, isConnected } = useAccount();

  const { data: balanceData } = useBalance({ address: address as `0x${string}`, chainId });

  const connectionState: WalletConnectionState = useMemo(() => {
    if (isConnecting) return "connecting";
    if (isConnected) return "connected";
    return "disconnected";
  }, [isConnecting, isConnected]);

  const shortAddress = useMemo(() => {
    if (!address) return undefined;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }, [address]);

  const isWrongNetwork = useMemo(() => {
    if (!targetChainId || !chainId) return false;
    return chainId !== targetChainId;
  }, [chainId, targetChainId]);

  const chainName = useMemo(() => {
    const chains: Record<number, string> = {
      1: "Ethereum",
      10: "Optimism",
      56: "BSC",
      137: "Polygon",
      8453: "Base",
      42161: "Arbitrum",
      43114: "Avalanche",
      80084: "Berachain Bartio",
      80094: "Berachain",
    };
    return chainId ? chains[chainId] || "Unknown" : undefined;
  }, [chainId]);

  return {
    isConnected,
    isConnecting,
    connectionState,
    address,
    shortAddress,
    username: null,
    nativeBalance: balanceData?.value ? balanceData.value.toString() : undefined,
    nativeBalanceFormatted: balanceData?.formatted,
    nativeSymbol: balanceData?.symbol,
    chainId,
    chainName,
    isWrongNetwork,
    disconnect: handleLogOut,
  };
}
